<template>
  <div>
    <label :for="generateId(id, 'add-row-')" class="custom-facility__row-checkbox">
      <input type="checkbox" 
        :id="generateId(id, 'add-row-')"
        @change="toggleCheckbox"
        v-model="checked"
      >
      Add column for {{ name }}
    </label>
    <div v-show="checked">
      <template v-for="option in options">
        <fieldset class="inset" 
          v-if="option === 'single_value'"
        >
          <label class="normal" 
            :for="generateId(id, 'row-option-single-')"
          >
            <input type="radio" 
              v-model="type"
              :value="option"
              :id="generateId(id, 'row-option-single-')"
              :name="generateId(id, 'row-options-')" 
            >
            Apply a single value to all months.
          </label>
          <fieldset class="mrgn-btm-0"
            v-show="type === 'single_value'"
          >
            <label>Enter Value:</label>
            <input type="text" class="currency mrgn-btm-0 mrgn-right-5" 
              :ref="'singleValue_' + id"
              @input="setSingleValue($event)"
              @blur="setSingleValue($event); formatValueInput($event);"
            >
            <button class="btn btn-sm"
              @click.prevent="addColumn(option)"
              :disabled="disableSingle"
            >{{ buttonAction }} Column</button>
          </fieldset>
        </fieldset>

        <fieldset class="inset" 
          v-if="option === 'multiple_values'"
        >
          <label class="normal" 
            :for="generateId(id, 'row-option-multi-')"
          >
            <input type="radio" 
              v-model="type"
              :value="option"
              :id="generateId(id, 'row-option-multi-')"
              :name="generateId(id, 'row-options-')"
            >
            Apply multiple values across the months. 
            <button class="btn btn-icon" 
              @click.prevent="toggleModal"
              v-show="type === 'multiple_values'"
            >Enter Values</button>
          </label>
          <fieldset class="mrgn-btm-0"
            v-show="type === 'multiple_values'"
          > 
            <div class="overlay" v-show="modalVisible">
              <transition name="slideDown"> 
                <div class="overlay__inner" v-show="modalVisible" @click.stop>
                  <button class="btn btn-icon overlay__close" aria-label="close"
                    @click.prevent="toggleModal"
                  ><i class="fa fa-close"></i></button>
                  <h3 class="sz-l">{{ name }}</h3>
                  <p class="mrgn-btm-0 sz-n">Enter desired value(s), applying each to a range of months.</p>
                  <table class="custom-facility__table custom-facility__table--fixed mrgn-btm-10">
                    <thead>
                      <tr>
                        <th class="label label--sm">&nbsp;</th>
                        <th>Value</th>
                        <th>Start Month</th>
                        <th>End Month</th>
                      </tr>
                    </thead>
                    <tr v-for="(valueRange, index) in valueRanges">
                      <td class="label label--sm label--with-button">
                        <label class="inline">{{ index + 1 }}</label>
                        <button class="btn btn-icon label__button"
                          v-if="index > 0"
                          @click.prevent="removeValueRange(index)"
                          title="Remove Value Range"
                        ><i class="fa fa-times-circle"></i></button>
                      </td>
                      <td>
                        <input type="text" 
                          @input="setRangeValue(index, $event)"
                          @blur="setRangeValue(index, $event); formatValueInput($event);"
                          :ref="'multiValue_'+id + '-' + index"
                        >
                      </td>
                      <td>
                        <div class="datepicker-custom"
                          @click="triggerChildDatepicker('startMonth-' + index, true)"
                        >
                          <datepicker 
                            @update-date="setRangeDate"
                            :startMonth="startMonth"
                            :endMonth="endMonth"
                            :prev="getStartMonth(index)"
                            :id="id"
                            :index="index"
                            :ref="'startMonth-' + index"
                            whichDate="start"
                          ></datepicker>
                        </div>
                      </td>
                      <td>
                        <div class="datepicker-custom"
                          @click="triggerChildDatepicker('endMonth-' + index, startMonthSet(index))"
                        >
                          <datepicker 
                            @update-date="setRangeDate"
                            :startMonth="startMonth"
                            :endMonth="endMonth"
                            :prev="getEndMonth(index)"
                            :id="id"
                            :disabled="!startMonthSet(index)"
                            :index="index"
                            :ref="'endMonth-' + index"
                            whichDate="end"
                          ></datepicker>
                        </div>
                      </td>
                    </tr>
                  </table>
                  <div class="mrgn-btm-30">
                    <button class="btn btn-icon btn-sm"
                      @click.prevent="addValueRange"
                      :disabled="!allowValueRanges"
                      v-show="allowValueRanges"
                    ><i class="fa fa-plus"></i> Add Value</button>
                  </div>

                  <button class="btn btn-sm mrgn-btm-0"
                    @click.prevent="addColumn(option)"
                  >{{ buttonAction }} Column</button>
                </div>
              </transition>
            </div>
         </fieldset>
        </fieldset>

        <fieldset class="inset"
          v-if="option === 'different_value'"
        >
          <label class="normal" 
            :for="generateId(id, 'row-option-different-')"
          >
            <input type="radio" 
              v-model="type"
              :value="option"
              :id="generateId(id, 'row-option-different-')"
              :name="generateId(id, 'row-options-')"
            >
            Enter a different value for each month.
          </label>
           <fieldset class="mrgn-btm-0"
             v-show="type === 'different_value'"
           >
            <button class="btn btn-sm"
             @click.prevent="addColumn(option)"
            >{{ buttonAction }} Column</button>
          </fieldset>
        </fieldset>

      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CustomFacilityProfileColumnDatepicker from './CustomFacilityProfileColumnDatepicker.vue'
import FacilityOptions from './FacilityOptions.vue'
import { formatNumber } from '../../../../common/helpers'

export default {
  props: [
    'id',
    'index',
    'name',
    'abbrev',
    'options',
    'existingColumns'
  ],
  components: {
    'datepicker': CustomFacilityProfileColumnDatepicker
  },
  data() {
    return {
      checked: false,
      type: null,
      singleValue: null,
      valueRanges: [
        {
          value: '',
          startMonth: '',
          endMonth: ''
        }
      ],
      maxValueRanges: 20,
      modalVisible: false,
    }
  },
  computed: {
    currentPricingRequest() {
      return this.$store.getters.currentPricingRequest
    },
    startMonth() {
      let date

      if (typeof this.currentPricingRequest.startMonth == 'string') {
        date = this.currentPricingRequest.startMonth
      } else {
        date = this.currentPricingRequest.startMonth.format('YYYY-M-D')
      }

      const dateParts = date.match(/(\d+)/g)

      return new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
    },
    endMonth() {
      return moment(this.startMonth).add(this.longestTermLength, 'months').date(0).toDate()
    },
    openDate() {
      return this.startMonth
    },
    termLength() {
      return this.$store.getters.termLength
    },
    customFacilityProfile() {
      return this.$store.getters.customFacilityProfile
    },
    storeValueRanges() {   
      if (this.customFacilityProfile) {
        
        if (this.customFacilityProfile.extraColumns && this.customFacilityProfile.extraColumns[this.index]) {
          return this.customFacilityProfile.extraColumns
        }
      }
    },
    activeTerms() {
      let activeTerms = this.termLength
      activeTerms = activeTerms.filter(item => {
        return item.value != ''
      })
      return activeTerms
    },
    longestTermLength() {   
      const longestTerm = this.activeTerms.sort(function(a, b) {
        if (parseInt(a.value) < parseInt(b.value)) {
          return 1;
        }
        return -1;
      })

      if (longestTerm.length) {
        return parseInt(longestTerm[0].value)

      } else if (this.customFacilityProfile.months && this.customFacilityProfile.months.length) {
        return this.customFacilityProfile.months.length
      }
    },
    columnExists() {
      let columnExists = this.existingColumns.filter(column => {
        return column.id === this.id
      })

      if (columnExists.length > 0) {
        return true
      }

      return false
    },
    valueRangeCount() {
      return this.valueRanges.length
    },
    allowValueRanges() {
      if (this.valueRangeCount < this.maxValueRanges) {
        return true
      }

      return false
    },
    buttonAction() {
      if (this.columnExists) {
        return 'Update'
      }
      return 'Add'
    },
    disableSingle() {
      const singleValue = this.$refs['singleValue_' + this.id]
      if (
        this.singleValue == null && (
          ! singleValue || 
          ! singleValue.value
        )
      ) {
        return true
      }
    }
  },
  methods: {
    populateField() {
      for (let i = 0; i < this.existingColumns.length; ++i) {
        if (this.existingColumns[i].id === this.id) {
          switch(this.existingColumns[i].type) {

            case 'single_value':
              this.$refs['singleValue_' + this.id].value = formatNumber(this.existingColumns[i].months[0].value, 2)
              break

            case 'multiple_values':
              if (this.existingColumns[i].valueRanges !== null) {
                for (let x = 0; x < this.existingColumns[i].valueRanges.length; ++x) {
                  this.$refs['multiValue_' + this.id + '-' + x].value = formatNumber(this.existingColumns[i].valueRanges[x].value, 2)
                }
                break
              }
          }
        }
      }
    },
    generateId(id, prefix) {
      return prefix + id
    },
    addColumn(type) {
      let defaultValue = 0
      let valueRanges = null

      switch(type) {
        case 'single_value':
          defaultValue = this.singleValue
          break

        case 'multiple_values':
          valueRanges = this.valueRanges
          break

        case 'different_value':
          break
      }

      if (!this.columnExists) {
        this.$emit('addColumn', { 
          id: this.id, 
          name: this.name, 
          abbrev: this.abbrev, 
          type: type,
          defaultValue: defaultValue,
          valueRanges: valueRanges
        })
      } else {
        this.$emit('updateColumn', {
          id: this.id,
          type: type,
          defaultValue: defaultValue,
          valueRanges: valueRanges
        })
      }
    },
    addValueRange() {
      if (this.allowValueRanges) {
        let range = {
          value: 0,
          startMonth: '',
          endMonth: ''
        }
        this.valueRanges.push(range)
      }
    },
    removeValueRange(index) {
      let ranges = this.valueRanges
      ranges.splice(index, 1)
      this.valueRanges = ranges
    },
    setRangeValue(index, event) {
      let value = formatNumber(event.target.value, 2, false)
      this.valueRanges[index].value = parseFloat(value)
    },
    setSingleValue(event) {
      let value = formatNumber(event.target.value, 2, false)
      this.singleValue = parseFloat(value)
    },
    formatValueInput(event) {
      if (event.target.value != '') {
        event.target.value = formatNumber(event.target.value, 2)
      }
    },
    setRangeDate(index, value, whichDate) {
      let property = ''

      switch(whichDate) {
        case 'start':
          property = 'startMonth'
          break

        case 'end':
          property = 'endMonth'

          if (moment(this.valueRanges[index].startMonth).isSameOrAfter(value)) {
            this.$store.dispatch('setNotification', { 
              type: 'error', 
              msg: 'You must select an end month that is after the start month.' 
            })
            this.$refs['endMonth-'+index][0].clearDate()
          }
          break
      }

      let conflict = false

      // Check to see if date falls inside of range that has already been selected
      if (this.valueRangeCount > 0) {
        for (let i = 0; i < this.valueRangeCount; ++i) {

          // Add a day to the value to accurately test for future values with moment.isBetween()
          let modifiedValue = new moment(value).add(1, 'days')

          if (moment(modifiedValue).isBetween(this.valueRanges[i].startMonth, this.valueRanges[i].endMonth)) {
            if (index != i) {
              conflict = true
            }
          }
        }
      }

      if (!conflict) {
        this.valueRanges[index][property] = value

      } else {
        this.$store.dispatch('setNotification', { 
          type: 'error', 
          msg: 'You must select a month that is not within a previously selected range.' 
        })
        this.valueRanges[index][property] = ''

        // Not quite right:
        this.$refs['startMonth-' + index][0].clearDate()
        this.$refs['endMonth-' + index][0].clearDate()
      }
    },
    triggerChildDatepicker(ref, datepickerEnabled) {
      if (datepickerEnabled) {
        this.$refs[ref][0].showDatepicker()
      }
    },
    startMonthSet(index) {
      if (this.valueRanges[index].startMonth) {
        return true
      }
      return false
    },
    getExistingColumn(key, value) {
      for (let i = 0; i < this.existingColumns.length; i++) {
        if (this.existingColumns[i][key] === value) {
          return this.existingColumns[i]
        }
      }
      return null;
    },
    toggleCheckbox(e) {
      if (!e.srcElement.checked) {
        this.$emit('removeColumn', this.id)
        this.$emit('deepTable')
        this.resetColumn()
      } else {
        this.$emit('shallowTable')
      }
    },
    toggleModal() {
      this.modalVisible = !this.modalVisible
    },
    hideModal() {
      this.modalVisible = false
    },
    resetColumn() {
      this.checked = false,
      this.type = null,
      this.singleValue = null
    },
    getStartMonth(index) {
      return this.valueRanges[index].startMonth
    },
    getEndMonth(index) {
      return this.valueRanges[index].endMonth
    }
  },
  watch: {
    columnExists() {
      if (!this.columnExists) {
        this.resetColumn()
      }
    },
    type() {
      if (this.type == 'single_value' && this.$refs['singleValue_'+this.id].value) {
        let value = formatNumber(this.$refs['singleValue_'+this.id].value, 2)
        this.singleValue = parseFloat(value)
      }
    }
  },
  created() {
    let existingColumn = this.getExistingColumn('id', this.id)

    if (existingColumn && existingColumn.valueRanges !== null && existingColumn.valueRanges.length > 0) {
      this.valueRanges = existingColumn.valueRanges
    }

    if (existingColumn) {
      this.checked = true
      this.type = existingColumn.type
    }
  },
  mounted() {
    if (Object.keys(this.existingColumns).length > 0) {
      this.populateField()
    }
  }
}
</script>
